import type { ComponentChildren } from 'preact'
import { ChevronRight } from 'lucide-preact'
import type { SendingDomainViewDto } from '@/types/sendingDomains'
import { dnsRecordCounts } from '@/utils/sendingDomainStatus'
import { DnsRecordsTable } from './DnsRecordsTable'
import { SendingDomainTrackingSection } from './SendingDomainTrackingSection'

interface DomainTechnicalDetailsProps {
  clientCompanyId: string
  domain: SendingDomainViewDto
  onUpdated: (domain: SendingDomainViewDto) => void
}

/**
 * The DNS records and tracking set-up, folded away under the page. The records stay open until every one is verified,
 * because until then they are the work; afterwards they only need to stay in place.
 */
export function DomainTechnicalDetails({ clientCompanyId, domain, onUpdated }: DomainTechnicalDetailsProps) {
  const { verified, total } = dnsRecordCounts(domain)
  const allVerified = total > 0 && verified === total
  const recordsMeta = total === 0 ? 'No records yet' : allVerified ? `${total} records, all verified` : `${verified} of ${total} verified`
  const trackingHost = domain.TrackingSubdomain ? `${domain.TrackingSubdomain}.${domain.Domain}` : null
  const recordsHint = allVerified
    ? 'Keep these in place at your DNS provider.'
    : domain.Status === 'NotStarted'
      ? 'Add these at your DNS provider, then press Check again to start the DNS check.'
      : 'Add these at your DNS provider. Verification picks them up once they resolve.'

  return (
    <div className="space-y-3">
      <Disclosure title="DNS records" meta={recordsMeta} open={!allVerified}>
        <p className="text-xs text-slate-600 dark:text-slate-400">{recordsHint}</p>
        <DnsRecordsTable records={domain.DnsRecords} />
      </Disclosure>
      <Disclosure
        title="Open and click tracking"
        meta={trackingHost
          ? (domain.ClickTrackingEnabled ? `Clicks tracked on ${trackingHost}` : `Tracking on ${trackingHost}`)
          : 'Not set up: some clicks may not register'}
        metaWarns={!trackingHost}
        open={false}
      >
        <SendingDomainTrackingSection clientCompanyId={clientCompanyId} domain={domain} onUpdated={onUpdated} />
      </Disclosure>
    </div>
  )
}

function Disclosure({ title, meta, metaWarns = false, open, children }: {
  title: string
  meta: string
  metaWarns?: boolean
  /** Open when first drawn, and whenever this turns true or false; the reader can toggle it in between. */
  open: boolean
  children: ComponentChildren
}) {
  return (
    <details open={open} className="group rounded-lg border border-slate-200 dark:border-slate-700">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-white dark:hover:bg-slate-800/60 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-90" aria-hidden="true" />
        {title}
        <span className={`ml-auto text-xs font-normal ${metaWarns ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>{meta}</span>
      </summary>
      <div className="space-y-3 px-4 pb-4 pt-1">{children}</div>
    </details>
  )
}
